// CLI `grid remix <ref> [dir]` — the "make my own copy" verb (mega-plan v2
// §8.1/§8.2).
//
// `remix` is distinct from `fork`. `fork` mints a copy that must land in the
// source's home grid (the byte-carrying path gated by `requireEntityAccess`;
// cross-grid attempts 403 with CROSS_ORG_RUNTIME_FORK_FORBIDDEN). `remix` is
// the visibility-gated path: anyone who can SEE an app (link / public /
// authenticated, or a grid member) may copy it into ANY grid they can build
// in — cross-grid included. The copy is minted `charged` with lineage back to
// the source (`forked_from` + `forked_from_grid_id`) and its cloudgrid.yaml is
// scrubbed of the source owner's secrets and connection credentials — the
// remixer supplies their own before plugging.
//
// Hits the live route `POST /api/v2/runtimes/:id/remix` (runtimes-ops.ts) —
// NOT the fork route. Body: `{ into_org_slug?, name?, source_version_id? }`.
//
// Two-step, like `pickup` (§8.2 "the remix verb auto-clones"): mint the copy,
// then download its (secret-stripped) source + cloudgrid.yaml and link the
// folder so the next `grid plug` deploys it. `--no-bind` records the mint only.

import { Command } from 'commander';

import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { notSignedInMessage } from '../active-org.js';
import { CloudGridError } from '../utils.js';
import { parseForkTarget, forkSourceRef } from './fork.js';
import { runClone } from './clone.js';

/**
 * Remix-route response shape (runtimes-ops.ts `/:id/remix`). Mirrors the fork
 * response plus the cross-grid lineage denorm `forked_from_grid_id` (§8.2).
 */
interface RemixResponse {
  entity_id: string;
  name: string;
  kind: 'app' | 'agent';
  org: { org_id?: string; slug?: string | null };
  grid_slug?: string | null;
  forked_from?: string | null;
  forked_from_version_id?: string | null;
  forked_from_grid_id?: string | null;
  current_version_id?: string | null;
}

export interface RunRemixOpts {
  client: IV2Client;
  /** The URL / slug / grid/slug / id the user typed for the source. */
  target: string;
  /** `--into-org <slug>` — the grid to create the copy in (destination). */
  intoOrg?: string;
  /** `--name <slug>` — override the new entity's slug. */
  name?: string;
  /** `--version <id>` — remix an older version of the source. */
  version?: string;
  /** Directory to lay the working tree into. Defaults to the entity name. */
  targetDir?: string;
  cwd?: string;
  /** Lay the working tree into a non-empty directory. */
  force?: boolean;
  /** Record the mint only — skip the local source download + link. */
  noBind?: boolean;
  /** `--json` — print the raw response instead of the human summary. */
  json?: boolean;
}

export async function runRemix(opts: RunRemixOpts): Promise<void> {
  const target = parseForkTarget(opts.target ?? '');
  const sourceRef = forkSourceRef(target);

  // Body per the live route. `into_org_slug` travels only when the user named
  // a destination with `--into-org`; when omitted, the server resolves it (a
  // single-grid caller lands there; a multi-grid caller gets INTO_ORG_REQUIRED,
  // mapped below to an --into-org hint). `name` / `source_version_id` are
  // optional overrides.
  const body: { into_org_slug?: string; name?: string; source_version_id?: string } = {};
  if (opts.intoOrg) body.into_org_slug = opts.intoOrg;
  if (opts.name) body.name = opts.name;
  if (opts.version) body.source_version_id = opts.version;

  let resp: RemixResponse;
  try {
    resp = await opts.client.post<RemixResponse>(
      `/api/v2/runtimes/${encodeURIComponent(sourceRef)}/remix`,
      body,
    );
  } catch (err) {
    if (err instanceof V2ApiError) throw mapRemixError(err, sourceRef);
    throw err;
  }

  if (opts.json) {
    process.stdout.write(JSON.stringify(resp) + '\n');
    return;
  }

  // The destination grid the copy landed in — the server's `grid_slug` is
  // authoritative (it reflects the resolved single-grid case even when the
  // user passed no --into-org); fall back to the org block, then the flag.
  const destSlug = resp.grid_slug ?? resp.org?.slug ?? opts.intoOrg ?? undefined;
  const where = destSlug ? ` into ${destSlug}` : '';

  const lines = [
    `Remixed ${sourceRef}${where} as ${resp.name} (${resp.entity_id}).`,
    `Your copy leaves out the source's secrets and connection credentials — add your own before you plug.`,
  ];
  if (resp.forked_from_version_id) {
    lines.push(`  Lineage: forked from version ${resp.forked_from_version_id}.`);
  }
  process.stdout.write(lines.join('\n') + '\n');

  if (opts.noBind) {
    // Mint-only: no working tree. Point the way to fetch it when they're ready.
    process.stdout.write(`  Next: grid pickup ${resp.entity_id}, then grid plug.\n`);
    return;
  }

  // Auto-clone the new copy (§8.2) — download its secret-stripped source +
  // cloudgrid.yaml and link the folder so the next `grid plug` deploys it.
  // Address by the server-confirmed entity_id (globally resolvable) and scope
  // the clone to the destination grid the copy lives in.
  await runClone({
    client: opts.client,
    target: resp.entity_id,
    targetDir: opts.targetDir,
    cwd: opts.cwd,
    org: destSlug,
    force: opts.force,
  });
}

/** Map remix-endpoint errors to plain, actionable messages (§23 voice). */
function mapRemixError(err: V2ApiError, sourceRef: string): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'REMIX_BLOCKED') {
    return new CloudGridError(
      err.message || `The owner turned off remixing for '${sourceRef}'.`,
    );
  }
  if (err.code === 'INTO_ORG_REQUIRED') {
    return new CloudGridError(
      'You belong to more than one grid. Pass --into-org <slug> to choose where the remix lands.',
    );
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      'Remixing into that grid needs admin or builder role there.',
    );
  }
  if (err.code === 'NOT_AUTHORIZED') {
    return new CloudGridError(
      "You're not a member of the destination grid. Pass --into-org <slug> naming a grid you belong to.",
    );
  }
  if (err.code === 'ORG_NOT_FOUND') {
    return new CloudGridError(err.message);
  }
  if (err.code === 'SOURCE_RETIRED') {
    return new CloudGridError(
      `'${sourceRef}' has not been deployed yet, so there is nothing to remix. Ask the owner to deploy it first.`,
    );
  }
  if (err.code === 'VERSION_NOT_FOUND') {
    return new CloudGridError(`No version with that id on '${sourceRef}'.`);
  }
  if (err.code === 'VALIDATION_FAILED') {
    return new CloudGridError(err.message);
  }
  if (err.code === 'RATE_LIMITED') {
    return new CloudGridError('Too many remixes today. Try again later.');
  }
  // The route returns RUNTIME_NOT_FOUND both for a genuine miss AND for the
  // existence-hiding path (a source the caller can't see, or the route dark).
  // One neutral message covers both — it never confirms the app exists.
  if (err.status === 404 || err.code.endsWith('_NOT_FOUND') || err.code === 'NOT_FOUND') {
    return new CloudGridError(`No app matched '${sourceRef}'. Check the id or grid/slug.`);
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run grid feedback if it persists.`,
  );
}

export const remixCommand: Command = new Command('remix')
  .description(
    "Make your own copy of an app in a grid you choose — cross-grid allowed — without the source's secrets, keeping lineage back to the original.",
  )
  .argument('<id|grid/slug>', 'App URL, id, or grid/slug to remix')
  .argument('[dir]', 'Directory to remix into (defaults to the app name)')
  .option('--into-org <slug>', 'Grid to create your copy in (required if you belong to more than one grid)')
  .option('--name <slug>', 'Name for your copy (defaults to the source name plus a suffix)')
  .option('--version <id>', 'Remix an older version of the source instead of HEAD')
  .option('--force', 'Remix into a non-empty directory')
  .option('--no-bind', 'Create the copy only — skip the source download and link')
  .option('--json', 'Print the result as JSON')
  .action(
    async (
      idOrSlug: string,
      dir: string | undefined,
      opts: {
        intoOrg?: string;
        name?: string;
        version?: string;
        force?: boolean;
        bind?: boolean;
        json?: boolean;
      },
    ) => {
      try {
        const client = await getV2Client(opts.intoOrg);
        await runRemix({
          client,
          target: idOrSlug,
          intoOrg: opts.intoOrg,
          name: opts.name,
          version: opts.version,
          targetDir: dir,
          force: opts.force,
          // Commander maps `--no-bind` to `opts.bind === false`.
          noBind: opts.bind === false,
          json: opts.json,
        });
      } catch (err) {
        await handleV2Error(err, { json: opts.json });
      }
    },
  );
